"use client";

import React, { memo } from "react";

// --- Types ---

interface ContentBlock {
  Heading?: string;
  Description?: string; // Can be plain text or HTML string
  List?: string;        // For the Outcome section bullets
}

interface CaseStudyDetailsProps {
  summary?: ContentBlock;
  challenge?: ContentBlock;
  outcome?: string;
}

// --- Sub-components ---

const SectionLabel = memo(({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`leading-none not-italic relative shrink-0 text-base tracking-[0.48px] uppercase ${className}`}>
    {children}
  </p>
));
SectionLabel.displayName = "SectionLabel";

// --- Main Component ---

const CaseStudyDetails = memo(({ summary, challenge, outcome }: CaseStudyDetailsProps) => {  
  // Helper to render text or HTML content while preserving typography
  const RenderContent = ({ content, className = "" }: { content?: string; className?: string }) => {
    if (!content) return null;
    return (
      <div 
        className={`text-lg md:text-xl font-light leading-relaxed text-gray-700 ${className} 
          [&_p]:mb-4 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-3`}
        dangerouslySetInnerHTML={{ __html: content }} 
      />
    );
  };

  return (
    <section className="container mx-auto py-12 space-y-12">
      
      {/* Summary Section */}
      <article className="space-y-6 text-black">
        <SectionLabel className="font-medium">Summary</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-light leading-tight tracking-tight">
          {summary?.Heading}
        </h2>
        <RenderContent content={summary?.Description} />
      </article>

      {/* Challenge Section */}
      <article className="space-y-6 text-black">
        <SectionLabel className="font-medium ">Challenge</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-light leading-tight tracking-tight">
          {challenge?.Heading}
        </h2>
        <RenderContent content={challenge?.Description} />
      </article>

      {/* Outcome Section */}
      <article className="space-y-12">
        <SectionLabel className="font-medium ">Outcome</SectionLabel>
        <div className="grid grid-cols-1 gap-6 !mt-6 text-black">          
          <RenderContent content={outcome} />
        </div>
      </article>
    </section>
  );
});

CaseStudyDetails.displayName = "CaseStudyDetails";

export default CaseStudyDetails;